import React, { useState, useEffect } from "react";

export default function ScheduleModalWeb({ open, onClose, onSave, initialWake = "", initialSleep = "", initialSchedule = null }) {
  // Prefill from what the user already has so we edit rather than re-ask. The
  // hardcoded 09:00/23:00 only apply when there is genuinely nothing on file.
  const hasWake = !!initialWake;
  const hasSleep = !!initialSleep;
  const hasSchedule = Array.isArray(initialSchedule) && initialSchedule.length > 0;
  const isFirstTime = !hasWake && !hasSleep && !hasSchedule;
  // Client spec: when a time already exists on the person it is shown READ ONLY —
  // the modal is for filling in what is missing, not editing what is set. Locked
  // per-field so a user who somehow has only one still edits the other. We submit
  // from state, not the inputs, so disabling them does not drop them from onSave.
  const wakeLocked = hasWake;
  const sleepLocked = hasSleep;
  const lockedStyle = (locked) => ({
    flex: 1, padding: "10px", borderRadius: "6px",
    background: locked ? "#eee" : "#f0f8ff",
    border: "1px solid #cce",
    color: locked ? "#666" : "inherit",
    cursor: locked ? "not-allowed" : "auto",
  });
  const [wakeTime, setWakeTime] = useState(initialWake || "09:00");
  const [sleepTime, setSleepTime] = useState(initialSleep || "23:00");
  const [schedule, setSchedule] = useState(
    hasSchedule ? initialSchedule.map((r) => [r[0] || "", r[1] || ""]) : [["", ""]]
  );
  const [saving, setSaving] = useState(false);

  // The modal stays mounted (returns null when closed), so useState seeds only
  // once — before currentUser/the payload have loaded. Re-seed from the current
  // props each time it opens, so an already-set user sees their real values.
  useEffect(() => {
    if (!open) return;
    setWakeTime(initialWake || "09:00");
    setSleepTime(initialSleep || "23:00");
    setSchedule(hasSchedule ? initialSchedule.map((r) => [r[0] || "", r[1] || ""]) : [["", ""]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialWake, initialSleep]);

  if (!open) return null;

  function addRow() {
    setSchedule((rows) => [...rows, ["", ""]]);
  }

  function removeRow() {
    setSchedule((rows) => (rows.length > 1 ? rows.slice(0, -1) : rows));
  }

  function updateRow(index, col, value) {
    setSchedule((rows) => {
      const next = rows.map((r) => [...r]);
      next[index][col] = value;
      return next;
    });
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const fmt = (t) => {
        if (!t) return "";
        const [h, m] = t.split(":").map(Number);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
      };
      const busy = schedule
        .map(([start, end]) => [fmt(start), fmt(end)])
        .filter(([s, e]) => s && e);
      await onSave(fmt(wakeTime), fmt(sleepTime), busy);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9750, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={onClose}>
      <form onSubmit={handleSave}
        style={{ background: "#fff", borderRadius: "14px", width: "min(520px,100%)", maxHeight: "90vh", overflow: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: "14px", position: "relative" }}
        onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose}
          style={{ position: "absolute", top: "12px", right: "12px", border: "none", background: "transparent", fontSize: "20px", cursor: "pointer", color: "#666" }}>✕</button>
        <p style={{ color: isFirstTime ? "#c00" : "#333", fontSize: "14px", margin: "0 24px 0 0", lineHeight: 1.5 }}>
          {isFirstTime
            ? "You have not added your daily schedule or sleep and wake up time — please add this to generate your flow plan."
            : "Your saved sleep and wake times are shown below. Add or update your daily schedule to generate your flow plan."}
        </p>
        <label style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ width: "90px", fontWeight: 600 }}>Wake Time</span>
          <input type="time" value={wakeTime} onChange={(e) => setWakeTime(e.target.value)}
            disabled={wakeLocked} readOnly={wakeLocked} style={lockedStyle(wakeLocked)} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ width: "90px", fontWeight: 600 }}>Sleep Time</span>
          <input type="time" value={sleepTime} onChange={(e) => setSleepTime(e.target.value)}
            disabled={sleepLocked} readOnly={sleepLocked} style={lockedStyle(sleepLocked)} />
        </label>
        <div style={{ fontWeight: 700, marginTop: "4px" }}>Busy Times Schedules</div>
        {schedule.map((row, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input type="time" value={row[0]} onChange={(e) => updateRow(i, 0, e.target.value)}
              style={{ flex: 1, padding: "10px", background: "#f0f8ff", border: "1px solid #cce", borderRadius: "6px" }} />
            <span>–</span>
            <input type="time" value={row[1]} onChange={(e) => updateRow(i, 1, e.target.value)}
              style={{ flex: 1, padding: "10px", background: "#f0f8ff", border: "1px solid #cce", borderRadius: "6px" }} />
          </div>
        ))}
        <div style={{ display: "flex", gap: "16px", justifyContent: "center" }}>
          <button type="button" onClick={addRow} style={{ border: "none", background: "transparent", color: "blue", cursor: "pointer", fontSize: "14px" }}>Add Schedule</button>
          <button type="button" onClick={removeRow} style={{ border: "none", background: "transparent", color: "red", cursor: "pointer", fontSize: "14px" }}>Remove Schedule</button>
        </div>
        <button type="submit" disabled={saving}
          style={{ alignSelf: "center", padding: "12px 32px", background: "blue", color: "#fff", border: "none", borderRadius: "10px", fontWeight: 600, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}>
          {saving ? "Saving…" : "Save"}
        </button>
      </form>
    </div>
  );
}
